<template>
  <div class="bg-gray-900 min-h-screen w-full">
    <div class="px-4 py-8 mx-auto container">
      <!-- Back button -->
      <button
        @click="$router.go(-1)"
        class="flex items-center text-gray-400 hover:text-blue-400 mb-6 transition-colors"
      >
        <span class="mr-2">&larr;</span> Nazad na proizvode
      </button>

      <!-- Loader -->
      <div v-if="showLoader" class="flex justify-center items-center py-20">
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
        ></div>
      </div>

      <!-- Error message -->
      <div v-else-if="error" class="bg-red-900 text-white p-4 rounded-lg mb-6">
        {{ error }}
      </div>

      <!-- Product details -->
      <div v-else-if="product" class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Product Gallery -->
        <ProductGallery
          :product="product"
          :current-image="currentImage || ''"
          :api-url="apiUrl || ''"
          @update:current-image="currentImage = $event"
          @open-lightbox="lightboxOpen = true"
        />

        <!-- Product Info -->
        <div
          class="flex flex-col bg-gray-800 rounded-lg p-6 shadow-lg product-info-container"
        >
          <ProductHeader :product="product" :format-price="formatPrice" />
          <ProductInfo :product="product" :format-date="formatDate" />
          <ProductSellerInfo
            :product="product"
            :is-current-user-product-owner="isCurrentUserProductOwner"
            @open-report-modal="showReportModal = true"
            @show-login-required-toast="showLoginRequiredToast"
          />

          <!-- Followers info -->
          <div class="flex justify-end mb-4">
            <div
              class="flex items-center space-x-1 bg-gray-700 bg-opacity-50 px-3 py-1 rounded-md followers-info"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-blue-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <span class="text-gray-300 text-sm">
                {{ followersCount }}
                {{ followersCount === 1 ? 'osoba prati' : 'osoba prati' }}
              </span>
            </div>
          </div>

          <!-- Only show ProductActions if the current user is not the product owner -->
          <ProductActions
            v-if="!isCurrentUserProductOwner"
            :following="following"
            :is-loading="isLoading"
            @contact-seller="handleContactSeller"
            @toggle-follow="handleToggleFollow"
          />

          <!-- Message when user is the owner -->
          <div
            v-else
            class="mt-4 p-3 bg-gray-700 rounded-lg text-gray-300 text-center owner-message"
          >
            Ovo je vaš oglas.
          </div>
        </div>
      </div>

      <!-- Product not found - samo ako je showNotFound true -->
      <div
        v-else-if="showNotFound"
        class="bg-gray-800 rounded-lg p-8 text-center text-gray-400"
      >
        Proizvod nije pronađen.
      </div>
    </div>

    <!-- Lightbox Modal -->
    <ProductLightbox
      v-if="lightboxOpen && currentImage && product"
      :product="product"
      :current-image="currentImage"
      :api-url="apiUrl || ''"
      @close="lightboxOpen = false"
      @update:current-image="currentImage = $event"
    />

    <!-- Report Modal -->
    <ReportModal
      v-if="showReportModal && product"
      :reported-user-id="product.user.id"
      :reported-username="product.user.username"
      @close="showReportModal = false"
      @report-submitted="handleReportSubmit"
    />

    <!-- Toast Notification -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import ToastNotification from '@/components/ToastNotification.vue';
import ProductGallery from '@/components/product/ProductGallery.vue';
import ProductHeader from '@/components/product/ProductHeader.vue';
import ProductInfo from '@/components/product/ProductInfo.vue';
import ProductSellerInfo from '@/components/product/ProductSellerInfo.vue';
import ProductActions from '@/components/product/ProductActions.vue';
import ProductLightbox from '@/components/product/ProductLightbox.vue';
import ReportModal from '@/components/ReportModal.vue';
import { useProductDetail } from '@/composables/useProductDetail';
import { useProductGallery } from '@/composables/useProductGallery';
import { useProductFollow } from '@/composables/useProductFollow';
import { debounce } from '@/utils/debounce';
import api from '@/services/api';

export default defineComponent({
  name: 'ProductDetailView',
  components: {
    ToastNotification,
    ProductGallery,
    ProductHeader,
    ProductInfo,
    ProductSellerInfo,
    ProductActions,
    ProductLightbox,
    ReportModal,
  },

  setup() {
    const store = useStore();

    // Debounced loader state
    const showLoader = ref(false);

    // Debounced "not found" state
    const showNotFound = ref(false);

    // Debounced functions for loader
    const showLoaderFn = () => {
      showLoader.value = true;
    };

    const hideLoaderFn = () => {
      showLoader.value = false;
    };

    // Debounced functions for "not found" message
    const showNotFoundFn = () => {
      showNotFound.value = true;
    };

    const hideNotFoundFn = () => {
      showNotFound.value = false;
    };

    // Create debounced functions with appropriate delays
    const debouncedShowLoader = debounce(showLoaderFn, 500);
    const debouncedShowNotFound = debounce(showNotFoundFn, 800); // Malo duži delay za "not found"

    // Immediate hide functions (with no delay)
    const immediateHideLoader = () => {
      debouncedShowLoader.cancel();
      hideLoaderFn();
    };

    const immediateHideNotFound = () => {
      debouncedShowNotFound.cancel();
      hideNotFoundFn();
    };

    const {
      product,
      loading,
      error,
      fetchProductDetails: fetchProduct,
      apiUrl,
      formatPrice,
      formatDate,
    } = useProductDetail();

    const { currentImage, lightboxOpen, handleKeyDown } =
      useProductGallery(product);

    const {
      following,
      followersCount,
      isLoading,
      handleToggleFollow,
      handleContactSeller,
      toast,
    } = useProductFollow(product);

    // Watch loading state and apply debounce
    watch(loading, (isLoading) => {
      if (isLoading) {
        // Reset "not found" state when loading starts
        immediateHideNotFound();
        // Only show loader if loading takes longer than the debounce timeout
        debouncedShowLoader();
      } else {
        immediateHideLoader();
      }
    });

    // Watch product state to handle "not found" scenario
    watch([product, loading, error], ([newProduct, isLoading, hasError]) => {
      if (!isLoading && !hasError && !newProduct) {
        // Show "not found" message only after debounce delay
        debouncedShowNotFound();
      } else {
        // Hide "not found" message immediately if we have product, loading, or error
        immediateHideNotFound();
      }
    });

    // Report modal state
    const showReportModal = ref(false);

    // Computed property to check if current user is the product owner
    const isCurrentUserProductOwner = computed(() => {
      const currentUser = store.getters['auth/currentUser'];

      if (!currentUser || !product.value) {
        return false;
      }

      return currentUser.id === product.value.user.id;
    });

    // Function to show toast notification when login is required
    const showLoginRequiredToast = (message: string) => {
      toast.value = {
        show: true,
        message,
        type: 'info',
      };
    };

    // Handle report submission
    const handleReportSubmit = async (reportData: {
      reason: string;
      description: string;
      reportedUserId: string;
    }) => {
      try {
        const response = await api.post(
          `/api/report-block/reports/${reportData.reportedUserId}`,
          {
            reason: reportData.reason,
            description: reportData.description,
          }
        );

        if (response.data.success) {
          toast.value = {
            show: true,
            message: 'Uspešno ste prijavili korisnika!',
            type: 'success',
          };
          showReportModal.value = false;
        } else {
          toast.value = {
            show: true,
            message: 'Došlo je do greške prilikom prijave. Pokušajte ponovo.',
            type: 'error',
          };
        }
      } catch (error) {
        console.error('Error submitting report:', error);
        toast.value = {
          show: true,
          message: 'Došlo je do greške prilikom prijave. Pokušajte ponovo.',
          type: 'error',
        };
      }
    };

    // Ensure the current user is loaded if authenticated
    if (store.getters['auth/isAuthenticated']) {
      store.dispatch('auth/ensureCurrentUser');
    }

    // Clean up on unmount
    onBeforeUnmount(() => {
      debouncedShowLoader.cancel();
      debouncedShowNotFound.cancel();
    });

    return {
      // Product data
      product,
      showLoader,
      showNotFound,
      error,
      apiUrl,
      fetchProduct, // Export the fetch function to use in methods
      formatPrice,
      formatDate,

      // Gallery
      currentImage,
      lightboxOpen,
      handleKeyDown,

      // Follow functionality
      following,
      followersCount,
      isLoading,
      handleToggleFollow,
      handleContactSeller,
      toast,

      // Report functionality
      showReportModal,
      handleReportSubmit,
      showLoginRequiredToast,

      // Owner check
      isCurrentUserProductOwner,
    };
  },

  computed: {
    productSlug(): string {
      return this.$route.params.id as string;
    },
  },

  methods: {
    // Define fetchProductDetails in methods to use this.productSlug
    fetchProductDetails() {
      if (this.productSlug) {
        this.fetchProduct(this.productSlug);
      }
    },
  },

  watch: {
    productSlug(newSlug) {
      if (newSlug) {
        this.fetchProductDetails();
      }
    },
  },

  mounted() {
    this.fetchProductDetails();
    // Add event listener for escape key to close lightbox
    window.addEventListener('keydown', this.handleKeyDown);
  },

  beforeUnmount() {
    // Remove event listener when component is destroyed
    window.removeEventListener('keydown', this.handleKeyDown);
  },
});
</script>

<style scoped>
/* Enhanced transitions and animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Add subtle hover effects to interactive elements */
button:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease-in-out;
}

/* Smooth transitions for all interactive elements */
button,
a,
.rounded-md,
.rounded-lg,
.rounded-full {
  transition: all 0.2s ease-in-out;
}

/* Enhanced shadow effects */
.shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2),
    0 4px 6px -2px rgba(0, 0, 0, 0.1);
}

/* Mobile responsiveness - similar to HomeView */
@media (max-width: 768px) {
  .container {
    padding: 0 15px;
  }

  /* Reduce padding on smaller screens */
  .px-4 {
    padding-left: 15px;
    padding-right: 15px;
  }

  .py-8 {
    padding-top: 30px;
    padding-bottom: 30px;
  }

  /* Grid layout adjustment */
  .grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  /* Product info container adjustments */
  .product-info-container {
    padding: 20px;
    margin: 0 -5px; /* Slight negative margin to use full width */
  }

  /* Followers info responsive */
  .followers-info {
    padding: 8px 12px;
    font-size: 0.875rem;
  }

  /* Owner message responsive */
  .owner-message {
    padding: 15px;
    font-size: 0.9rem;
  }

  /* Back button responsive */
  button {
    padding: 8px 12px;
    font-size: 0.9rem;
  }

  /* Error message responsive */
  .bg-red-900 {
    padding: 15px;
    font-size: 0.9rem;
  }

  /* Toast notification positioning */
  .toast-notification {
    margin: 0 15px;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 10px;
  }

  /* Further reduce padding on very small screens */
  .px-4 {
    padding-left: 10px;
    padding-right: 10px;
  }

  .py-8 {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  /* Product info container for very small screens */
  .product-info-container {
    padding: 15px;
    margin: 0 -10px;
    border-radius: 8px;
  }

  /* Followers info for very small screens */
  .followers-info {
    padding: 6px 10px;
    font-size: 0.8rem;
  }

  .followers-info .h-4 {
    height: 14px;
    width: 14px;
  }

  /* Owner message for very small screens */
  .owner-message {
    padding: 12px;
    font-size: 0.85rem;
  }

  /* Back button for very small screens */
  button {
    padding: 6px 10px;
    font-size: 0.85rem;
  }

  /* Reduce gap between elements */
  .gap-8 {
    gap: 15px;
  }

  /* Loader adjustment */
  .py-20 {
    padding-top: 60px;
    padding-bottom: 60px;
  }

  /* Error message for very small screens */
  .bg-red-900 {
    padding: 12px;
    font-size: 0.85rem;
    border-radius: 6px;
  }

  /* Modal adjustments for small screens */
  .fixed {
    margin: 10px;
  }
}

/* Ensure text doesn't overflow containers */
@media (max-width: 640px) {
  * {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  /* Prevent horizontal scrolling */
  body {
    overflow-x: hidden;
  }

  /* Ensure full width utilization */
  .w-full {
    width: 100%;
    max-width: 100%;
  }

  /* Responsive text sizes */
  .text-sm {
    font-size: 0.8rem;
  }

  .text-base {
    font-size: 0.9rem;
  }

  .text-lg {
    font-size: 1rem;
  }

  .text-xl {
    font-size: 1.1rem;
  }

  .text-2xl {
    font-size: 1.3rem;
  }

  /* Ensure buttons are touch-friendly */
  button,
  a {
    min-height: 44px;
    min-width: 44px;
  }
}

/* Landscape orientation on mobile */
@media (max-width: 768px) and (orientation: landscape) {
  .py-8 {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  .product-info-container {
    max-height: 70vh;
    overflow-y: auto;
  }
}

/* High DPI displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .shadow-lg {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15),
      0 4px 6px -2px rgba(0, 0, 0, 0.08);
  }
}
</style>
